import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ArrowLeft, CalendarClock, Eye, EyeOff, Maximize, Minimize, MonitorPlay, X, ChevronRight, KanbanSquare, Flag as FlagIcon, LayoutDashboard, LogOut, Merge, Pencil, PlusCircle, Timer, Undo2 } from 'lucide-react'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { asProject } from '@/features/permissions/ability'
import { useIdentity } from '@/features/permissions/AbilityProvider'
import { usePermission } from '@/features/permissions/usePermission'
import { paths } from '@/routes/paths'
import { cn } from '@/lib/utils'
import type { Client, Member } from '@/types/entities'
import { lifecycleApi, useLifecycle, useLifecycleWrite, useProjectDeals, useRelatedProjects, useUnlinkProjectDeal } from './api'
import { PROJECT_TYPE_LABEL } from '@/components/composites/status-badge'
import { MergeDialog, UndoDialog } from './ChangeDialogs'
import { CycleDialog, EditLifecycleDialog, EscalateDialog, ExtendPilotDialog, GateDialog, MetricsEditor, OffboardDialog } from './Dialogs'
import { LifecycleGantt } from './Gantt'
import { MetricsListEditor, RiskEditor } from './PlanEditors'
import { ActionRegistry } from './Registry'
import {
  HEALTH_DOT,
  HEALTH_LABEL,
  HEALTH_SOFT,
  PHASE_LABEL,
  PRODUCT_STATUS_LABEL,
  TEAM_LONG,
  ageInDays,
  daysUntil,
  longDate,
  phaseLine,
  productLabel,
  type Dashboard,
} from './model'

type DialogKind = 'gate' | 'cycle' | 'extend' | 'offboard' | 'escalate' | 'edit' | 'stakeholders' | 'team' | 'merge' | 'undo' | null

/**
 * One Client Project's dashboard (handbook: Dashboard and action registry). Top to bottom: the
 * header with the stakeholders, red flags (only when raised), requirements and problem, the
 * Gantt, the action registry, risks beside the change log, and success metrics last. It stays
 * live for the life of the account.
 */
/**
 * The client project dashboard. `presenting` is the same page for screen sharing with the client:
 * the app's navigation, top bar, notifications and the Office are hidden (AppShell renders the
 * page bare on the `/present` route) and everything on the page keeps working.
 */
export function ClientProjectPage() {
  return <ClientProjectView presenting={false} />
}

/** Presentation mode: the same dashboard, rendered bare by the app shell for screen sharing. */
export function ClientProjectPresentPage() {
  return <ClientProjectView presenting />
}

function ClientProjectView({ presenting }: { presenting: boolean }) {
  const { projectId = '', workspaceSlug = '' } = useParams()
  const query = useLifecycle(projectId)
  if (query.isPending) return <LoadingState label="Loading the client dashboard…" />
  if (query.error) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        <p className="mt-3 text-center text-sm">
          <Link className="text-brand hover:underline" to={paths.projectPage(workspaceSlug, projectId)}>Open the project page instead</Link>
        </p>
      </div>
    )
  }
  return <DashboardView data={query.data} presenting={presenting} />
}

/** The browser's full screen, for presenting: on while in it, and Esc (or the button) leaves. */
function useFullscreen() {
  const [on, setOn] = useState(() => typeof document !== 'undefined' && Boolean(document.fullscreenElement))
  useEffect(() => {
    const sync = () => setOn(Boolean(document.fullscreenElement))
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])
  const toggle = () => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined)
    else void document.documentElement.requestFullscreen?.().catch(() => undefined)
  }
  return { on, toggle, supported: typeof document !== 'undefined' && Boolean(document.fullscreenEnabled) }
}

function DashboardView({ data, presenting }: { data: Dashboard; presenting: boolean }) {
  const { workspaceSlug = '' } = useParams()
  const me = useIdentity()
  const { can } = usePermission()
  const { project, lifecycle: lc, tasks, plan, health } = data
  const canManage = project.ownerId === me.userId || project.createdBy === me.userId || can('update', asProject(project))
  const [dialog, setDialog] = useState<DialogKind>(null)
  const [editing, setEditing] = useState({ risks: false, metrics: false })
  const [now] = useState(() => Date.now())
  const fullscreen = useFullscreen()
  /** In presentation mode the internal health signals (badge, escalation banner, red flags) start hidden. */
  const [showInternal, setShowInternal] = useState(false)
  const internal = !presenting || showInternal

  const membersQuery = useQuery({ queryKey: qk.members(), queryFn: () => apiGet<{ items: Member[] }>('members') })
  const clientsQuery = useQuery({ queryKey: qk.clients(), queryFn: () => apiGet<{ items: Client[] }>('clients') })
  const people = useMemo(() => (membersQuery.data?.items ?? []).filter((m) => !m.disabled).map((m) => ({ id: m.id, label: m.displayName, hint: m.email, photoUrl: m.photoUrl })), [membersQuery.data])
  const nameOf = useMemo(() => new Map((membersQuery.data?.items ?? []).map((m) => [m.id, m.displayName])), [membersQuery.data])
  const client = clientsQuery.data?.items.find((c) => c.id === project.clientId)

  const logContact = useLifecycleWrite(project.id, () => lifecycleApi.patch(project.id, { lastContactAt: new Date().toISOString() }), 'Client contact logged')
  const flagTasks = useLifecycleWrite(project.id, (keys?: string[]) => lifecycleApi.flagTasks(project.id, keys), 'Linked task added to the registry')
  const clearFlag = useLifecycleWrite(project.id, (key: string) => lifecycleApi.clearFlag(project.id, key), 'Flag cleared')

  const toTarget = daysUntil(lc.targetPostSalesAt, now)
  const currentPhase = lc.phases.find((p) => p.status === 'active' && p.phase === lc.currentPhase && (p.cycle === lc.currentCycle || p.phase === 'post_sales'))
  const pilotRunning = lc.currentPhase === 'pilot' && currentPhase && currentPhase.extensionDays === 0
  const reachedPostSales = lc.phases.some((p) => p.phase === 'post_sales' && p.status !== 'upcoming')
  const cycleName = lc.cycles.find((c) => c.n === lc.currentCycle)?.name

  return (
    <div className="mx-auto grid w-full max-w-[1500px] gap-4 px-2 pb-10 md:px-6">
      {/* 1. Header */}
      <section className="grid gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
        {presenting ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              aria-pressed={showInternal}
              title="The health badge, the escalation banner and the red flags are internal. They start hidden while presenting."
              onClick={() => setShowInternal((v) => !v)}
            >
              {showInternal ? <EyeOff className="size-4" /> : <Eye className="size-4" />} {showInternal ? 'Hide health and flags' : 'Show health and flags'}
            </Button>
            {fullscreen.supported ? (
              <Button size="sm" variant="ghost" onClick={fullscreen.toggle}>
                {fullscreen.on ? <Minimize className="size-4" /> : <Maximize className="size-4" />} {fullscreen.on ? 'Exit full screen' : 'Full screen'}
              </Button>
            ) : null}
            <Button asChild size="sm" variant="outline">
              <Link to={paths.clientProject(workspaceSlug, project.id)}><X className="size-4" /> Exit presentation</Link>
            </Button>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Link to={paths.projects(workspaceSlug)} className="inline-flex items-center gap-1 text-sm text-brand hover:underline">
              <ArrowLeft className="size-3.5" /> All projects
            </Link>
            <div className="flex flex-wrap items-center gap-2">
              {canManage ? (
                <>
                  <Button size="sm" variant="ghost" title="This turns out to be part of another account" onClick={() => setDialog('merge')}><Merge className="size-4" /> Merge into…</Button>
                  <Button size="sm" variant="ghost" title="Undo the last conversion, opening from the CRM, or merge" onClick={() => setDialog('undo')}><Undo2 className="size-4" /> Undo…</Button>
                </>
              ) : null}
              <Button asChild size="sm" variant="outline" title="Hide the menu, top bar, notifications and the Office while you share your screen">
                <Link to={paths.clientProjectPresent(workspaceSlug, project.id)}><MonitorPlay className="size-4" /> Presentation mode</Link>
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand">{client?.name ?? 'Client'} · Client Project</p>
            <h1 className="text-xl font-semibold text-balance">{project.name}</h1>
            <p className="text-sm text-muted-foreground">Owner {project.ownerId ? nameOf.get(project.ownerId) ?? 'AM' : '—'} · started {longDate(lc.startAt)}</p>
          </div>
          {internal ? <span className={cn('inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold', HEALTH_SOFT[health.level])}>
            <span className={cn('size-2.5 rounded-full', HEALTH_DOT[health.level])} aria-hidden />
            {HEALTH_LABEL[health.level]}
            <span className="font-normal">· {health.flags.length} flag{health.flags.length === 1 ? '' : 's'}</span>
          </span> : null}
        </div>
        {/* Stakeholders: who we deal with, right under who owns the account */}
        <div className="rounded-lg border border-border-soft bg-surface-sunken/50 p-3">
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold">Stakeholders</h2>
            <span className="num rounded-full bg-card px-2 text-xs text-muted-foreground">{lc.contacts.length}</span>
            {canManage ? <Button size="sm" variant="outline" className="ml-auto h-7" onClick={() => setDialog('stakeholders')}><Pencil className="size-3.5" /> Edit</Button> : null}
          </div>
          {lc.contacts.length ? (
            <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {lc.contacts.map((c) => (
                <li key={c.id} className="rounded-lg border border-border bg-card px-3 py-2">
                  <p className="text-sm font-medium">
                    {c.name}
                    {c.sponsor ? <span className="ml-1.5 rounded bg-brand-softer px-1.5 py-px text-[11px] font-semibold text-brand">Sponsor</span> : null}
                    {c.decisionMaker ? <span className="ml-1.5 rounded bg-neutral-soft px-1.5 py-px text-[11px] font-semibold">Decision-maker</span> : null}
                    {c.left ? <span className="ml-1.5 rounded bg-danger-soft px-1.5 py-px text-[11px] font-semibold text-danger">Left</span> : null}
                  </p>
                  <p className="text-xs text-muted-foreground">{[c.role, c.email].filter(Boolean).join(' · ') || '—'}</p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No client stakeholders yet.{canManage ? ' Use Edit to add the sponsor and decision-maker.' : ''}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <p><span className="font-semibold">Operations & Support:</span> {lc.team.ops.map((id) => nameOf.get(id) ?? 'Someone').join(', ') || '—'}</p>
            <p><span className="font-semibold">Product & Engineering:</span> {lc.team.pe.map((id) => nameOf.get(id) ?? 'Someone').join(', ') || '—'}</p>
            {canManage ? <button type="button" className="font-medium text-brand hover:underline" onClick={() => setDialog('team')}>Edit team</button> : null}
          </div>
        </div>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Current phase">
            {phaseLine(lc)}
            {lc.currentCycle > 1 && cycleName && lc.currentPhase !== 'post_sales' ? <span className="block text-xs font-normal text-muted-foreground">{cycleName}</span> : null}
          </Stat>
          <Stat label="Phase ends">{lc.status === 'closed' ? '—' : currentPhase?.endAt ? longDate(currentPhase.endAt) : 'Ongoing'}</Stat>
          <Stat label="Target Post-Sales">
            {longDate(lc.targetPostSalesAt)}
            {!reachedPostSales && toTarget !== null ? <span className={cn('block text-xs font-normal', toTarget < 0 ? 'text-danger' : 'text-muted-foreground')}>{toTarget < 0 ? `${-toTarget} days past` : `${toTarget} days to go`}</span> : null}
          </Stat>
          <Stat label="Last client contact">
            {longDate(lc.lastContactAt)}
            {canManage && lc.status !== 'closed' ? (
              <button type="button" className="block text-xs font-medium text-brand hover:underline" disabled={logContact.isPending} onClick={() => logContact.mutate(undefined)}>Log contact today</button>
            ) : null}
          </Stat>
        </dl>
        <div className="flex flex-wrap gap-1.5">
          {lc.products.map((p) => (
            <span key={p.key} className={cn('rounded-full border px-2.5 py-0.5 text-xs', p.status === 'live' ? 'border-success bg-success-soft text-success' : p.status === 'contracted' ? 'border-brand bg-brand-softer text-brand' : 'border-border text-muted-foreground')}>
              <span className="font-semibold">{productLabel(p.key)}</span> · {PRODUCT_STATUS_LABEL[p.status]}
            </span>
          ))}
        </div>
        {canManage && lc.status !== 'closed' ? (
          <div className="flex flex-wrap gap-2 border-t border-border-soft pt-3">
            {lc.currentPhase !== 'post_sales' ? (
              <Button size="sm" onClick={() => setDialog('gate')}>
                <ChevronRight className="size-4" /> {lc.currentPhase === 'offboarding' ? 'Close project' : `Pass ${PHASE_LABEL[lc.currentPhase]} gate`}
              </Button>
            ) : null}
            {pilotRunning ? <Button size="sm" variant="outline" onClick={() => setDialog('extend')}><Timer className="size-4" /> Extend pilot</Button> : null}
            {lc.currentPhase === 'post_sales' && lc.status === 'active' ? <Button size="sm" onClick={() => setDialog('cycle')}><PlusCircle className="size-4" /> New cycle</Button> : null}
            <Button asChild size="sm" variant="outline">
              <Link to={`${paths.projectPage(workspaceSlug, project.id)}?tab=board`}><KanbanSquare className="size-4" /> Work items and kanban</Link>
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDialog('edit')}><Pencil className="size-4" /> Edit details</Button>
            {lc.status === 'active' ? <Button size="sm" variant="ghost" className="text-danger" onClick={() => setDialog('offboard')}><LogOut className="size-4" /> Start offboarding</Button> : null}
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 border-t border-border-soft pt-3">
            <Button asChild size="sm" variant="outline">
              <Link to={`${paths.projectPage(workspaceSlug, project.id)}?tab=board`}><KanbanSquare className="size-4" /> Work items and kanban</Link>
            </Button>
          </div>
        )}
      </section>

      {internal && health.needsEscalation && canManage ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertTriangle className="size-5 shrink-0" />
          <span className="flex-1"><strong>This project is Red.</strong> Escalate to the Head of Customer Success within 24 hours, with a written recovery plan.</span>
          <Button size="sm" variant="outline" onClick={() => setDialog('escalate')}>Record escalation</Button>
        </div>
      ) : null}

      {/* Red flags, above the timeline, only when there are any */}
      {internal && health.flags.length > 0 ? (
      <Panel
        title="Red flags"
        count={health.flags.length}
        action={canManage && health.flags.some((f) => !f.linkedTaskId) ? <Button size="sm" variant="outline" disabled={flagTasks.isPending} onClick={() => flagTasks.mutate(undefined)}>Add linked tasks for all</Button> : null}
      >
          <ul className="grid gap-2">
            {health.flags.map((f) => {
              const linked = f.linkedTaskId ? tasks.find((t) => t.id === f.linkedTaskId) : null
              return (
                <li key={f.key} className={cn('flex flex-wrap items-start gap-3 rounded-lg border px-3 py-2.5', f.level === 'red' ? 'border-danger bg-danger-soft' : 'border-warning bg-warning-soft')}>
                  <FlagIcon className={cn('mt-0.5 size-4 shrink-0', f.level === 'red' ? 'text-danger' : 'text-warning')} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{f.title}</p>
                    <p className="text-xs text-muted-foreground">{f.detail}</p>
                    <p className="mt-1 flex flex-wrap gap-2 text-xs">
                      <span className="rounded bg-card px-1.5 py-px font-semibold">{TEAM_LONG[f.team]}</span>
                      <span className="text-muted-foreground">{ageInDays(f.since, now)} day{ageInDays(f.since, now) === 1 ? '' : 's'} old</span>
                      {linked ? <span className="text-muted-foreground">Linked: T-{linked.seq} · {linked.status === 'done' ? 'Done' : 'open'}</span> : null}
                    </p>
                  </div>
                  {canManage ? (
                    f.linkedTaskDone ? (
                      <Button size="sm" disabled={clearFlag.isPending} onClick={() => clearFlag.mutate(f.key)}>Confirm and clear</Button>
                    ) : !f.linkedTaskId ? (
                      <Button size="sm" variant="outline" disabled={flagTasks.isPending} onClick={() => flagTasks.mutate([f.key])}>Add linked task</Button>
                    ) : null
                  ) : null}
                </li>
              )
            })}
          </ul>
        <p className="mt-2 text-tiny text-text-muted">A linked task is due in 1 business day for red and 3 for yellow. The flag clears when its task is Done and the AM confirms.</p>
      </Panel>
      ) : null}

      {/* Requirements and problem, above the timeline */}
      <Panel title="Requirements and problem">
        <div className="grid gap-4 md:grid-cols-2">
          <div><p className="text-xs font-semibold uppercase text-text-muted">Problem</p><p className="whitespace-pre-wrap text-sm">{lc.problem || '—'}</p></div>
          <div><p className="text-xs font-semibold uppercase text-text-muted">Aim and strategy</p><p className="whitespace-pre-wrap text-sm">{plan.aim || '—'}</p></div>
          <div className="md:col-span-2"><p className="text-xs font-semibold uppercase text-text-muted">Requirements</p><p className="whitespace-pre-wrap text-sm">{lc.requirements || '—'}</p></div>
          <div className="md:col-span-2">
            <p className="text-xs font-semibold uppercase text-text-muted">Scope</p>
            <ul className="mt-1 grid gap-1 text-sm">
              {lc.scope.map((s) => <li key={s.id} className={cn(!s.inScope && 'text-muted-foreground line-through')}>{s.inScope ? '✓' : '✕'} {s.text}</li>)}
            </ul>
          </div>
        </div>
      </Panel>

      {/* Gantt */}
      <Panel title="Timeline" icon={<CalendarClock className="size-4" />}>
        <LifecycleGantt phases={lc.phases} cycles={lc.cycles} milestones={plan.milestones} target={lc.targetPostSalesAt} />
      </Panel>

      {/* Action registry */}
      <Panel title="Action registry" count={tasks.length}>
        <ActionRegistry projectId={project.id} lifecycle={lc} tasks={tasks} risks={plan.risks} people={people} canManage={canManage} myId={me.userId} />
      </Panel>

      {/* The client's earlier projects, kept with their work */}
      <CrmDeals projectId={project.id} />
      <AdditionalRequirements projectId={project.id} nameOf={nameOf} />

      {/* Risks and the change log, side by side */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Risk register"
          count={plan.risks.length}
          action={canManage && !editing.risks ? <Button size="sm" variant="outline" onClick={() => setEditing((e) => ({ ...e, risks: true }))}><Pencil className="size-3.5" /> Edit</Button> : null}
        >
          {editing.risks ? (
            <RiskEditor projectId={project.id} plan={plan} onDone={() => setEditing((e) => ({ ...e, risks: false }))} />
          ) : plan.risks.length === 0 ? <Empty>No risks recorded.{canManage ? ' Use Edit to add one.' : ''}</Empty> : (
            <ul className="grid gap-2">
              {plan.risks.map((r) => (
                <li key={r.id} className="rounded-lg border border-border px-3 py-2">
                  <p className="text-sm font-medium">{r.text}</p>
                  <p className="text-xs text-muted-foreground">
                    {r.likelihood ? `Likelihood ${r.likelihood}` : `Severity ${r.severity}`}{r.impact ? ` · impact ${r.impact}` : ''} · {r.ownerTeam ? TEAM_LONG[r.ownerTeam] : 'AM'} ·{' '}
                    <span className={cn(r.status === 'occurred' && 'font-semibold text-danger')}>{r.status}</span>
                  </p>
                  {r.mitigation ? <p className="mt-0.5 text-xs">Mitigation: {r.mitigation}</p> : <p className="mt-0.5 text-xs text-warning">No mitigation yet</p>}
                </li>
              ))}
            </ul>
          )}
        </Panel>
        <Panel title="Change log" count={lc.changeLog.length}>
          <ol className="grid max-h-96 gap-2 overflow-y-auto">
            {lc.changeLog.slice().reverse().map((e, i) => (
              <li key={`${e.at}-${i}`} className="border-l-2 border-border pl-2.5">
                <p className="text-xs">{e.what}</p>
                <p className="text-tiny text-text-muted">{longDate(e.at)} · {nameOf.get(e.by) ?? 'Someone'}</p>
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      {/* Success metrics, at the bottom */}
      <Panel
        title="Success metrics"
        count={plan.metrics.length}
        action={canManage && !editing.metrics ? <Button size="sm" variant="outline" onClick={() => setEditing((e) => ({ ...e, metrics: true }))}><Pencil className="size-3.5" /> Edit</Button> : null}
      >
        {editing.metrics ? (
          <MetricsListEditor projectId={project.id} plan={plan} onDone={() => setEditing((e) => ({ ...e, metrics: false }))} />
        ) : (
          <MetricsEditor key={plan.version ?? 0} projectId={project.id} plan={plan} canManage={canManage} />
        )}
      </Panel>

      {dialog === 'gate' ? <GateDialog data={data} onClose={() => setDialog(null)} /> : null}
      {dialog === 'cycle' ? <CycleDialog data={data} onClose={() => setDialog(null)} /> : null}
      {dialog === 'extend' ? <ExtendPilotDialog data={data} onClose={() => setDialog(null)} /> : null}
      {dialog === 'offboard' ? <OffboardDialog data={data} onClose={() => setDialog(null)} /> : null}
      {dialog === 'merge' ? <MergeDialog projectId={project.id} name={project.name} onClose={() => setDialog(null)} /> : null}
      {dialog === 'undo' ? <UndoDialog projectId={project.id} onClose={() => setDialog(null)} /> : null}
      {dialog === 'escalate' ? <EscalateDialog data={data} onClose={() => setDialog(null)} /> : null}
      {dialog === 'edit' ? <EditLifecycleDialog data={data} people={people} onClose={() => setDialog(null)} /> : null}
      {dialog === 'stakeholders' ? <EditLifecycleDialog data={data} people={people} initialTab="stakeholders" onClose={() => setDialog(null)} /> : null}
      {dialog === 'team' ? <EditLifecycleDialog data={data} people={people} initialTab="team" onClose={() => setDialog(null)} /> : null}
    </div>
  )
}

/**
 * The same client and region's other projects: what the account ran before Client Projects (kept
 * by the migration, with their work items), and any later additional requirements. Each opens
 * its own project page and board.
 */
function AdditionalRequirements({ projectId, nameOf }: { projectId: string; nameOf: Map<string, string> }) {
  const { workspaceSlug = '' } = useParams()
  const related = useRelatedProjects(projectId)
  // In their order on the timeline: the ones carried as cycles first, numbered; any others after.
  const items = [...(related.data ?? [])].sort((a, b) => (a.cycle ?? Infinity) - (b.cycle ?? Infinity))
  const rank = (cycle: number | null | undefined) => (cycle ? items.filter((x) => x.cycle).findIndex((x) => x.cycle === cycle) + 1 : 0)
  if (!related.isPending && !items.length) return null
  return (
    <Panel title="Additional requirements" count={items.length}>
      {related.isPending ? <Empty>Loading…</Empty> : (
        <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <li key={p.id}>
              <Link to={`${paths.projectPage(workspaceSlug, p.id)}?tab=board`} className="block rounded-lg border border-border px-3 py-2 hover:border-brand hover:bg-accent">
                <span className="block text-sm font-medium text-brand">{rank(p.cycle) ? <span className="mr-1 text-muted-foreground">#{rank(p.cycle)}</span> : null}{p.name}</span>
                <span className="block text-xs text-muted-foreground">
                  {PROJECT_TYPE_LABEL[p.type]} · {p.ownerId ? (nameOf.get(p.ownerId) ?? 'Owner') : 'No owner'} · since {longDate(p.createdAt)}{p.archived ? ' · archived' : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

/**
 * The GoHighLevel deals behind this project, read-only. Until Closed Won the Sales deal moves the
 * project's phase; after that Tapwork moves the Post-sales deal. Edit deals in the CRM pages.
 */
function CrmDeals({ projectId }: { projectId: string }) {
  const { workspaceSlug = '' } = useParams()
  const deals = useProjectDeals(projectId)
  const unlink = useUnlinkProjectDeal(projectId)
  const items = deals.data?.items ?? []
  if (!items.length) return null
  return (
    <Panel title="From the CRM" count={items.length}>
      <ul className="grid gap-2 sm:grid-cols-2">
        {items.map((d) => (
          <li key={d.dealId} className="rounded-lg border border-border px-3 py-2">
            <span className="block text-sm font-medium">{d.name ?? 'Deal no longer in GoHighLevel'}</span>
            <span className="block text-xs text-muted-foreground">
              {d.pipeline ?? 'GoHighLevel'} · {d.stage ?? '—'}
              {d.status && d.status !== 'open' ? ` (${d.status})` : ''}
              {d.value ? ` · ${d.value.toLocaleString()}` : ''}
              {d.owner ? ` · ${d.owner}` : ''}
            </span>
            <span className="mt-1 block text-tiny text-text-muted">
              Moves the phase: {d.inCharge}
              {d.accountKey ? (
                <>
                  {' · '}
                  <Link className="text-brand hover:underline" to={paths.crmAccount(workspaceSlug, d.accountKey)}>Open the account</Link>
                </>
              ) : null}
              {' · '}
              <button
                type="button"
                className="text-brand hover:underline disabled:opacity-50"
                disabled={unlink.isPending}
                title="Take this deal off the project. An open Sales deal shows as a draft again."
                onClick={() => unlink.mutate(d.dealId)}
              >
                Unlink
              </button>
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  )
}

/** Shown on a Client Project's own page, pointing at its dashboard. */
export function ClientDashboardLink({ projectId }: { projectId: string }) {
  const { workspaceSlug = '' } = useParams()
  return (
    <Button asChild variant="outline" size="sm">
      <Link to={paths.clientProject(workspaceSlug, projectId)}>
        <LayoutDashboard className="size-3.5" /> Return to the client dashboard
      </Link>
    </Button>
  )
}

function Panel({ title, count, action, icon, children }: { title: string; count?: number; action?: ReactNode; icon?: ReactNode; children: ReactNode }) {
  return (
    <section className="min-w-0 rounded-xl border border-border bg-card p-4 shadow-sm">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        {icon}
        <h2 className="text-base font-semibold">{title}</h2>
        {count !== undefined ? <span className="num rounded-full bg-surface-sunken px-2 text-xs text-muted-foreground">{count}</span> : null}
        {action ? <div className="ml-auto">{action}</div> : null}
      </header>
      {children}
    </section>
  )
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="rounded-lg bg-surface-sunken px-3 py-2">
      <dt className="text-tiny font-medium uppercase tracking-wide text-text-muted">{label}</dt>
      <dd className="text-sm font-semibold">{children}</dd>
    </div>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="py-2 text-sm text-muted-foreground">{children}</p>
}
